import './applySearchYearPage.scss';

import { ThemeProvider } from '@mui/material/styles';
import rangeRight from 'lodash/rangeRight';
import toString from 'lodash/toString';
import { ReactNode } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Header } from '../../../components/Header/Header';
import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { RadioGroup } from '../../../components/RadioGroup/RadioGroup';
import { RadioOptionClickEvent } from '../../../components/RadioGroup/types/RadioOptionClickEvent';
import { isValidClickEvent } from '../../../components/utils/isValidClickEvent';
import useApplyStore from '../../../store/applyStore/useApplyStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { next, previous } = useWorkflowStore();

  const { year, setYear } = useApplyStore();

  const { register, handleSubmit, watch } = useForm({
    defaultValues: {
      yearValue: year,
    },
  });

  const YEARS_OF_VEHICLES_TO_SHOW = 20;
  const INCLUDE_NEXT_YEAR_OFFSET = 2;

  // Present 20 years of vehicles starting from this year whilst including next year.
  // lodash range does not include its end parameter, so we add 2 to include next year
  const vehicleYears = rangeRight(
    new Date().getFullYear() - YEARS_OF_VEHICLES_TO_SHOW,
    new Date().getFullYear() + INCLUDE_NEXT_YEAR_OFFSET,
  ).map(toString);

  const formSubmit = (values: FieldValues): void => {
    const { yearValue } = values;

    if (yearValue) {
      setYear(yearValue);

      navigate(next());
    }
  };

  const previousStep = (): void => {
    navigate(previous());
  };

  const radioOptionClicked = (event: RadioOptionClickEvent<string>): void => {
    if (
      !('target' in event && 'value' in event.target) ||
      !isValidClickEvent(event)
    ) {
      return;
    }

    formSubmit({ yearValue: event.target.value });
  };

  return (
    <ThemeProvider theme={theme}>
      <div className="page apply-search-year-page">
        <Header
          progress={useWorkflowStore.getState().computed.progress}
          showDrawerToggle
          onBackClick={previousStep}
        />

        <main>
          <form
            data-testid={locator.pages.applySearchYear.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1 data-testid={locator.pages.applySearchYear.form.h1}>
                {t('ApplySearchYearPage.title')}
              </h1>
              <h2 data-testid={locator.pages.applySearchYear.form.h2}>
                {t('ApplySearchYearPage.subtitle')}
              </h2>

              <div style={{ marginBottom: '3rem' }}>
                <RadioGroup
                  name="yearValue"
                  options={vehicleYears.map((yearValue, index) => ({
                    label: yearValue,
                    value: yearValue,
                    testId:
                      locator.pages.applySearchYear.form.getRadioOption(index),
                  }))}
                  register={register}
                  testId={locator.pages.applySearchYear.form.radioGroupRoot}
                  variant="stacked-compact"
                  watch={watch}
                  onClick={radioOptionClicked}
                />
              </div>

              {/* allows form submission using the keyboard without a visible button*/}
              <button
                aria-hidden="true"
                data-testid={
                  locator.pages.applySearchYear.form.accessibilitySubmit
                }
                style={{ display: 'none' }}
                type="submit"
              />
            </div>
            <footer data-testid={locator.pages.applySearchYear.footer.root}>
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </div>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'ApplySearchYearPage';
